// Which companion paths the iPhone app draws itself, and which it leaves to
// a web screen (docs/ios-build-spec.md §6). One definition, used by the
// embedded web app to hand a navigation to native, and pinned for the
// Swift (DeepLink) and Python (companion_paths) ports by
// shared-fixtures/deeplinks.json, which scripts/gen-shared-fixtures.mjs
// produces by running these very functions.
//
// Native owns the screens used standing up: Today, Record, the chat list
// and a standard agent's thread, Meetings, Todos and More. Everything else
// under /companion is a page that changes weekly and stays a web screen.
// A few paths on this origin are neither: single-use sign-in links, the
// admin desktops, agents' deployed apps, the handsets' screens and the API
// always open in Safari (spec §6), never as Today.
//
// Pure functions, no React, no browser globals. Self-test:
// nativePaths.selftest.ts (runs with `npm run check:format`).

import { companionLocationFor } from "./paths";

export type NativeDestination =
  | { kind: "today" }
  | { kind: "record" }
  | { kind: "chat-list" }
  /**
   * A thread. Whether `agent` is a standard agent or a subagent blueprint
   * is only known at runtime (the agent directory); a blueprint's chat is a
   * tab of its page under Subagents, so the app opens `blueprintPath` as a
   * web screen instead (the query rides along, as ChatThread does it).
   */
  | { kind: "chat"; agent: string; blueprintPath: string }
  /** `open` and `view` as the query has them (an empty value counts as absent, as on the web). */
  | { kind: "meetings"; open: string | null; view: string | null }
  | { kind: "todos" }
  | { kind: "more" }
  /** A web screen: the companion path as given, with its query and hash. */
  | { kind: "web"; path: string }
  /** Not the app's page at all (isSafariOnlyPath): the path as given, opened in Safari. */
  | { kind: "safari"; path: string };

export type NativeKind = NativeDestination["kind"];

const ROOT = "/companion";

// The tab screens and Todos, by their one path segment.
const SCREENS: Record<string, NativeKind> = {
  today: "today",
  record: "record",
  chat: "chat-list",
  meetings: "meetings",
  todos: "todos",
  more: "more",
};

// Paths that are served from this origin but are not the dashboard: a
// /login-assist/<token> link (single use: the token must reach that page;
// mapped like a desktop path it opened Today and was lost), a /set-password
// link (its single-use token is in the fragment, for the web page), a /connect
// link an agent sends to sign in to a product for it (the same: a ticket in
// the fragment, and the vendor's consent must open in Safari), a /book link
// (a scheduling page a stranger books on, or its /book/manage#lat_bk_… change
// link: a public web page, never an app screen), a /signup page (Google
// refuses its sign-in inside the app's web view), the people-search
// and data desktops (noVNC behind the edge), agents' deployed apps, the
// emulated handsets' screens (/phone/, /phone-sentient/, …), the API, the
// websockets and the edge's own auth endpoints (/_auth/…). Whole segments,
// apart from /phone*. The AASA file excludes the same paths from universal
// links.
const SAFARI_SEGMENTS = ["/login-assist", "/set-password", "/connect", "/book", "/signup", "/people-browser", "/data-vnc", "/agent-dev", "/api", "/ws", "/_auth"];
const SAFARI_PREFIX = "/phone";

/** True for a path that always opens in Safari, never in the app (spec §6). */
export function isSafariOnlyPath(pathname: string): boolean {
  if (pathname.startsWith(SAFARI_PREFIX)) return true;
  return SAFARI_SEGMENTS.some((root) => pathname === root || pathname.startsWith(root + "/"));
}

/** True for "/companion" and anything below it ("/companionx" is a desktop path). */
export function isCompanionPath(pathname: string): boolean {
  return pathname === ROOT || pathname.startsWith(ROOT + "/");
}

function decodeSegment(s: string): string {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

// The segments after /companion, one trailing slash ignored (react-router
// matches /companion/today/ as /companion/today); null for a path outside
// the companion. Exact and case-sensitive on purpose: an odd spelling is
// left to the web screen, which always renders something sensible.
function segments(pathname: string): string[] | null {
  if (!isCompanionPath(pathname)) return null;
  const path = pathname.length > ROOT.length + 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  if (path === ROOT || path === ROOT + "/") return [];
  return path.slice(ROOT.length + 1).split("/");
}

/**
 * Where a companion location opens in the app. A path outside /companion
 * is not the app's business and is returned as a web path unchanged
 * (callers map desktop paths first: resolveAppPath).
 */
export function nativeDestination(pathname: string, search: string, hash = ""): NativeDestination {
  const segs = segments(pathname);
  const web: NativeDestination = { kind: "web", path: pathname + normalSearch(search) + normalHash(hash) };
  if (segs === null) return web;
  // /companion itself redirects to Today.
  if (segs.length === 0) return { kind: "today" };
  if (segs.length === 1) {
    const kind = SCREENS[segs[0]];
    if (!kind) return web;
    if (kind === "meetings") {
      const q = new URLSearchParams(normalSearch(search));
      return { kind, open: q.get("open") || null, view: q.get("view") || null };
    }
    return { kind } as NativeDestination;
  }
  if (segs.length === 2 && segs[0] === "chat" && segs[1] !== "") {
    const agent = decodeSegment(segs[1]);
    return { kind: "chat", agent, blueprintPath: `${ROOT}/subagents/${encodeURIComponent(agent)}/chat${normalSearch(search)}` };
  }
  return web;
}

/** Whether native draws this companion path (a blueprint's thread aside, which only runtime knows). */
export function isNativeOwned(pathname: string): boolean {
  return nativeDestination(pathname, "").kind !== "web";
}

/** The short form the fixtures and logs use: "today", "chat:julia", "meetings", "web", "safari". */
export function nativeKey(d: NativeDestination): string {
  return d.kind === "chat" ? `chat:${d.agent}` : d.kind;
}

// A bare "?" or "#" is no query or hash at all, as location reports them.
const normalSearch = (s: string) => (s === "?" ? "" : s);
const normalHash = (h: string) => (h === "#" ? "" : h);

/** A path with its query and hash split off the way location splits them. */
export function splitPath(input: string): { pathname: string; search: string; hash: string } {
  const h = input.indexOf("#");
  const beforeHash = h < 0 ? input : input.slice(0, h);
  const hash = normalHash(h < 0 ? "" : input.slice(h));
  const q = beforeHash.indexOf("?");
  const pathname = q < 0 ? beforeHash : beforeHash.slice(0, q);
  const search = normalSearch(q < 0 ? "" : beforeHash.slice(q));
  return { pathname: pathname || "/", search, hash };
}

/**
 * A link from anywhere (a notification's path, a widget, a universal link):
 * its companion location (a desktop path mapped the way a phone browser is
 * redirected, the hash kept; a companion path as it is) and where the app
 * opens it. A Safari-only path is not mapped: it comes back as given.
 */
export function resolveAppPath(input: string): { companion: string; destination: NativeDestination } {
  const { pathname, search, hash } = splitPath(input);
  if (isSafariOnlyPath(pathname)) {
    const path = pathname + search + hash;
    return { companion: path, destination: { kind: "safari", path } };
  }
  const loc = isCompanionPath(pathname) ? { pathname, search } : companionLocationFor(pathname, search);
  return {
    companion: loc.pathname + loc.search + hash,
    destination: nativeDestination(loc.pathname, loc.search, hash),
  };
}
